import { AnyAbility, subject as caslSubject } from '@casl/ability';
import { useAbility } from '@casl/react';

import { AbilityContext } from '@/contexts/02-Authenticated/ability';
import { AppAbility, PERMISSIONS, PermissionTuple } from '@/shared/permissions';

type Instance = { id: string };

export function evaluatePermission(
    ability: AppAbility | AnyAbility,
    permission: PermissionTuple,
    options?: {
        instance?: Instance;
        field?: string;
    },
) {
    const [action, subject] = permission;

    if (options?.instance) {
        const instance = caslSubject(subject, { id: options.instance.id });

        if (options?.field) {
            return ability.can(action, instance, options.field);
        }

        return ability.can(action, instance);
    }

    if (options?.field) {
        return ability.can(action, subject, options.field);
    }

    return ability.can(action, subject);
}

export function getAccessState(
    ability: AppAbility | AnyAbility,
    permission: PermissionTuple,
    options?: {
        instance?: Instance;
        field?: string;
    },
) {
    const allowed = evaluatePermission(ability, permission, options);

    return {
        allowed,
        hidden: !allowed,
        disabled: !allowed,
        readOnly: !allowed,
        // Advanced
        canRead: allowed,
        canInteract: allowed,
        canMutate: allowed,
        // UX
        reason: allowed ? null : 'INSUFFICIENT_PERMISSIONS',
    };
}

export const useCASL = () => {
    const ability = useAbility(AbilityContext);

    const rules = ability.rules;

    const canRaw = ability.can.bind(ability);

    const access = (permission: PermissionTuple, instance?: { id: string }) =>
        getAccessState(ability, permission, { instance });
    const canPermission = (permission: PermissionTuple, id?: string) =>
        evaluatePermission(ability, permission, id ? { instance: { id } } : undefined);

    return {
        ability,
        rules,
        can: canPermission,
        canRaw,
        // Workspace
        canManageWorkspace: () => canPermission(PERMISSIONS.WORKSPACE.MANAGE),
        canInviteUsers: () => canPermission(PERMISSIONS.WORKSPACE.INVITE_USER),
        canUpdateWorkspace: () => canPermission(PERMISSIONS.WORKSPACE.UPDATE),
        // Resource + Nested Resource Factories
        canRead: canPermission,
        canUpdate: canPermission,
        canManage: canPermission,
        canComment: canPermission,
        // UI
        accessState: (p: PermissionTuple, id?: string) => access(p, id ? { id } : undefined),
    };
};
